var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { makeObservable } from 'mobx';
import { computedFn } from 'mobx-utils';
import { AttachedTrait } from '../../../../trait/attachedtrait/AttachedTrait';
import { Trait } from '../../../../trait/trait/Trait';
import { AttachedMedSeqTrait } from '../../../../trait/attachedspecific/AttachedMedSeqTrait/AttachedMedSeqTrait';
import { AttachedActionTrait } from '../../../../trait/attachedspecific/AttachedActionTrait';
import { AttachedSlotTrait } from '../../../../trait/attachedspecific/AttachedSlotTrait/AttachedSlotTrait';
import { runtime } from '../../../../runtime/Runtime';
import { AttachedImage } from '../../../../content/image/AttachedImage';
import { AttachedText } from '../../../../content/text/AttachedText';
import { AttachedSkeletonTrait } from '../../../../trait/attachedspecific/AttachedSkeletonTrait';
import { TraitTypeSkeletonKey } from '../../../../../registry/trait/traitTypes/TraitTypeSkeletonKey';
import { TraitTypeActionKey } from '../../../../../registry/trait/traitTypes/TraitTypeActionKey';
import { TraitTypeSlotKey } from '../../../../../registry/trait/traitTypes/TraitTypeSlotKey';
import { TraitTypeMedSeq } from '../../../../../registry/trait/traitTypes/TraitTypeMedSeq';
// import { AttachedHearPattern } from '../../../../trait/attachedspecific/AttachedHearPattern/AttachedHearPattern';
import { sortedSet } from '../../../../../utils/mobx/decorators/customComputed';
import { flatten, uniq } from '../../../../../utils/helper/lodash_replacements';
import { AttachedBox } from '../../../../content/box/AttachedBox';
import { AttachedGap } from '../../../../content/gap/AttachedGap';
import { memoize } from '../../../../../utils/mobx/decorators/memoize';
import { AttachedExportTrait } from '../../../../trait/attachedspecific/AttachedExportTrait';
import { AttachedVideo } from '../../../../content/video/AttachedVideo';
import { AttachedAudio } from '../../../../content/audio/AttachedAudio';
import { AttachedSound } from '../../../../content/sound/AttachedSound';
import { AttachedTable } from '../../../../content/table/AttachedTable';
import { bound } from '../../../../../utils/decorators/basic';
export class AspectAttach {
    parent;
    constructor(parent) {
        makeObservable(this);
        this.parent = parent;
    }
    get aspect() {
        return this.parent;
    }
    attachTrait(traitOrObject) {
        let trait;
        if (traitOrObject instanceof Trait) {
            trait = traitOrObject;
        }
        else {
            trait = runtime.class.Trait.fromSerialized(traitOrObject);
            trait.isGenerated = true;
            trait.createdBy = traitOrObject.createdBy;
        }
        if (!trait.reg) {
            console.error(`kein Trait registry Eintrag für: ${trait.key}`, this.aspect.readableIdentifier);
            trait = Trait.fromSerialized({ parent: this.aspect, key: 'waitForPackage', value: '' });
        }
        const init = { trait, parent: this.aspect };
        if (trait.key === 'image') {
            return new AttachedImage(init);
        }
        if (trait.key === 'text') {
            return new AttachedText(init);
        }
        if (trait.key === 'box') {
            return new AttachedBox(init);
        }
        if (trait.key === 'table') {
            return new AttachedTable(init);
        }
        if (trait.key === 'gap') {
            return new AttachedGap(init);
        }
        if (trait.key === 'video') {
            //  || trait.key === 'bgVideo'
            return new AttachedVideo(init);
        }
        if (trait.key === 'audio') {
            return new AttachedAudio(init);
        }
        if (trait.key === 'sound') {
            return new AttachedSound(init);
        }
        /*
        if (trait.key === 'hearPattern') {
          return new AttachedHearPattern(init);
        }
    */
        if (trait.key === 'exportaspect') {
            return new AttachedExportTrait(init);
        }
        if (trait.reg instanceof TraitTypeSkeletonKey) {
            return new AttachedSkeletonTrait(init);
        }
        if (trait.reg instanceof TraitTypeActionKey) {
            return new AttachedActionTrait(init);
        }
        if (trait.reg instanceof TraitTypeSlotKey) {
            return new AttachedSlotTrait(init);
        }
        if (trait.reg instanceof TraitTypeMedSeq) {
            return new AttachedMedSeqTrait(init);
        }
        return new AttachedTrait(init);
    }
    get attachTraitTransformer() {
        return computedFn(this.attachTrait);
    }
    cache = new Map();
    toAttached(t) {
        const cached = this.cache.get(t);
        if (cached) {
            this.potentiallyLoadStylesheets(cached);
            return cached;
        }
        const at = this.attachTraitTransformer(t);
        this.potentiallyLoadStylesheets(at);
        if (at.key !== 'waitForPackage') {
            this.cache.set(t, at);
            // setTimeout(() => this.cache.delete(t), 15000);
        }
        return at;
    }
    potentiallyLoadStylesheets(at) {
        if (runtime.isBrowserRuntime && at.reg && at.reg.stylesheet) {
            const stylesheetUrls_raw = at.reg.stylesheet(at.context);
            const stylesheetUrls = Array.isArray(stylesheetUrls_raw) ? stylesheetUrls_raw : [stylesheetUrls_raw];
            for (const stylesheetUrl of stylesheetUrls) {
                runtime.browserRuntime.browserIncludes.includeCSSFileOnce(stylesheetUrl);
            }
        }
    }
    get attachedTraits_unsorted() {
        return uniq(flatten(this.aspect.aspectTraits.traitKeysWithDerived.map(traitKey => this.aspect.global.aspectKey(traitKey).keyAttached.attachedTraits)));
    }
    get attachedTraits() {
        return this.attachedTraits_unsorted.concat().sort((at1, at2) => at2.prio - at1.prio);
    }
    get attachedObject() {
        return this.attachedTraits.reduce((returnObj, at) => {
            if (at.trait.isMultiple) {
                if (!returnObj[at.key]) {
                    returnObj[at.key] = [];
                }
                returnObj[at.key].unshift(at);
            }
            if (!returnObj[at.key]) {
                returnObj[at.key] = at;
            }
            return returnObj;
        }, {});
    }
}
__decorate([
    bound
], AspectAttach.prototype, "attachTrait", null);
__decorate([
    memoize
], AspectAttach.prototype, "attachTraitTransformer", null);
__decorate([
    bound
], AspectAttach.prototype, "toAttached", null);
__decorate([
    sortedSet
], AspectAttach.prototype, "attachedTraits", null);
